// A condition visit's status and inspection date: the device's pure rules
// (visit-lifecycle.ts does the asking). The API's visit-status.ts decides.
import type { DeviceRecord } from './device-db';

/** hig_visitstatus, in order. */
export const VISIT_STEPS = ['Draft', 'Complete', 'Validated', 'Issued'] as const;
export type VisitStep = (typeof VISIT_STEPS)[number];

const LOCKED_STEPS: readonly string[] = ['Validated', 'Issued'];

/** The visit's status; Draft when blank. Pure. */
export const visitStep = (visit: Pick<DeviceRecord, 'fields'>): VisitStep => {
  const status = visit.fields.hig_status;
  return (VISIT_STEPS as readonly unknown[]).includes(status) ? (status as VisitStep) : 'Draft';
};

/** A validated (or issued) visit's ratings, areas and ticks can't change until it is reopened. Pure. */
export const visitLocked = (visit: Pick<DeviceRecord, 'fields'>): boolean => LOCKED_STEPS.includes(visitStep(visit));

/** What can be done to a visit at its status. Pure. */
export function nextChanges(step: VisitStep): ('complete' | 'validate' | 'reopen')[] {
  if (step === 'Draft') return ['complete'];
  if (step === 'Complete') return ['validate', 'reopen'];
  if (step === 'Validated') return ['reopen'];
  return [];
}

/** The inspection date's day for a date picker (YYYY-MM-DD, the device's own day); '' when unset. Pure. */
export function inspectedDay(iso: unknown): string {
  const time = typeof iso === 'string' ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(time) ? '' : new Date(time).toLocaleDateString('en-CA');
}

/** A moment as the device's own day (28/10/2026); null when unset. Pure. */
export function localDay(iso: unknown): string | null {
  const time = typeof iso === 'string' ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(time) ? null : new Date(time).toLocaleDateString('en-AU');
}

/** A picked day as the visit's inspection moment: midday on the device, so the day reads the same in any Australian zone. Pure. */
export const inspectedOnFor = (day: string): string => new Date(`${day}T12:00:00`).toISOString();
